import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useChatRecovery } from '../state/ChatRecoveryContext';
import { useAuth } from '../state/AuthContext';
import useChatLogic from './ChatLogic';
import { useCapabilities } from '../state/CapabilitiesContext';
import ChatInput from './Components/ChatInput';
import ChatConversation from './Components/ChatConversation';
import ChatSidebar from './Components/ChatSidebar';

const ChatComponent = () => {
  const { chatId: chatIdFromRoute } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const capabilities = useCapabilities();
  const recoveryStore = useChatRecovery();
  const messageEndRef = useRef(null);
  const conversationRef = useRef(null);
  const { isAuthenticated, currentUser, status: authStatus, refreshUser, setUserFromCallback } = useAuth();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const handleChatIdChange = useCallback(
    (newChatId) => {
      if (!newChatId) {
        navigate('/chat', { replace: true });
        return;
      }
      const targetPath = `/chat/${newChatId}`;
      if (chatIdFromRoute !== newChatId) {
        navigate(targetPath, { replace: true });
      }
    },
    [chatIdFromRoute, navigate]
  );

  const {
    input,
    sendStatus,
    cancelResponse,
    reconcileSend,
    setInput,
    messages,
    isLoading,
    handleSendMessage,
    handleKeyDown,
    sessionInfo,
    chatId,
    chatError,
    isChatLoading,
    retryCreateChat,
    startNewChat,
    startAuthSignIn,
    isAuthRequired,
    setIsAuthRequired,
    threads,
    threadsLoading,
    threadsError,
    threadsHasMore,
    loadMoreThreads,
    openThread,
  } = useChatLogic(chatIdFromRoute, handleChatIdChange, isAuthenticated, {
    initialDraft: typeof location.state?.publicDraft === 'string' ? location.state.publicDraft : '',
    onFeatureDisabled: capabilities.disable,
    recoveryStore,
    recoveryIdentity: authStatus === 'unauthenticated' ? 'guest'
      : authStatus === 'authenticated' && currentUser?.email ? `user:${currentUser.email}` : null,
  });

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatError]);

  useEffect(() => {
    const container = conversationRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages, chatError]);

  useEffect(() => {
    if (isAuthenticated && isAuthRequired) {
      setIsAuthRequired(false);
    }
  }, [isAuthenticated, isAuthRequired, setIsAuthRequired]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsSidebarVisible(false);
    }
  }, [isAuthenticated]);

  const isChatBusy = isLoading || isChatLoading;
  const userLabel = isAuthenticated
    ? `👤 ${currentUser?.displayName || sessionInfo?.role || 'User'}`
    : '👤 Guest';
  const identityUnverified = recoveryStore && (authStatus === 'error' ||
    (authStatus === 'authenticated' && !currentUser?.email));
  const disabledReason = identityUnverified ? 'Verify your session before recovering this conversation'
    : ['uncertain', 'reconciling'].includes(sendStatus)
    ? 'Check conversation history before sending again'
    : isAuthRequired
    ? 'Authentication required to continue'
    : chatError ||
      (isChatLoading
        ? 'Loading chat...'
        : !chatId
          ? 'Initializing chat...'
          : '');
  const messageFadeMask = 'linear-gradient(to bottom, transparent, black 4rem, ' +
    'black calc(100% - 4rem), transparent)';

  return (
    <div data-testid="chat-root" className="relative w-full h-full max-w-6xl mx-auto">
      <div className="h-full flex">
        {isAuthenticated && isSidebarVisible && (
          <ChatSidebar
            id="chat-history-sidebar"
            threads={threads}
            isLoading={threadsLoading}
            error={threadsError}
            hasMore={threadsHasMore}
            onLoadMore={loadMoreThreads}
            activeChatId={chatId}
            onOpenThread={openThread}
          />
        )}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-hidden">
            <div
              ref={conversationRef}
              data-testid="chat-message-scroll"
              className="h-full overflow-y-auto pl-4 pr-4 pt-6 pb-2 scroll-smooth"
              style={{
                maskImage: messageFadeMask,
                WebkitMaskImage: messageFadeMask,
              }}
            >
              <ChatConversation
                messages={messages}
                messageEndRef={messageEndRef}
                sessionInfo={sessionInfo}
                chatError={chatError}
                onAuthSignIn={startAuthSignIn}
              />
            </div>
          </div>

          <div data-testid="chat-controls" className="px-4 pt-1 pb-4">
            {sessionInfo && (
              <div className="mb-2 flex justify-end text-sm text-gray-200 drop-shadow">
                {isAuthenticated ? (
                  <button
                    type="button"
                    aria-controls="chat-history-sidebar"
                    aria-expanded={isSidebarVisible}
                    onClick={() => setIsSidebarVisible((visible) => !visible)}
                    className={
                      'rounded-md px-2 py-1 hover:bg-gray-700/70 focus:outline-none ' +
                      'focus:ring-2 focus:ring-blue-400'
                    }
                  >
                    {userLabel}
                  </button>
                ) : (
                  <span>{userLabel}</span>
                )}
              </div>
            )}
            {chatError && (
              <div
                className={
                  'bg-red-500 bg-opacity-10 border border-red-500 text-red-200 rounded-xl ' +
                  'px-4 py-3 mb-3 flex flex-col sm:flex-row sm:items-center ' +
                  'sm:justify-between space-y-2 sm:space-y-0 sm:space-x-4'
                }
              >
                <p className="text-sm">{chatError}</p>
                <div className="flex space-x-2">
                  {chatIdFromRoute && (
                    <button
                      type="button"
                      onClick={() => startNewChat()}
                      className={
                        'px-3 py-2 text-sm rounded-lg bg-gray-600 text-white ' +
                        'hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400'
                      }
                    >
                      Start New Chat
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => retryCreateChat()}
                    className={
                      'px-3 py-2 text-sm rounded-lg bg-red-600 text-white ' +
                      'hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400'
                    }
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}
            {identityUnverified && <div role="status">
              <p>Your session could not be verified. Your retained draft stays closed until it is checked.</p>
              <button onClick={refreshUser}>Retry session check</button>
            </div>}
            {isLoading && <button className="rounded px-3 py-2 bg-gray-700" onClick={cancelResponse}>
              Stop receiving response</button>}
            {['uncertain', 'reconciling'].includes(sendStatus) && <div role="status">
              <p>The send result is uncertain. Stopping does not undo server processing.</p>
              <button onClick={reconcileSend} disabled={sendStatus === 'reconciling'}>
                {sendStatus === 'reconciling' ? 'Checking history…' : 'Check conversation history'}</button>
            </div>}
            {sendStatus === 'retry' && <p role="status">History does not contain this send yet.
              Review the draft before explicitly sending again.</p>}
            {sendStatus === 'recorded' && <p role="status">This send is recorded in history.</p>}
            <ChatInput
              input={input}
              setInput={setInput}
              handleKeyDown={handleKeyDown}
              handleSendMessage={handleSendMessage}
              isLoading={isChatBusy}
              disabledReason={disabledReason}
            />
          </div>
        </div>

        {process.env.NODE_ENV === 'test' && (
          <div className="sr-only">
            <div data-testid="isAuthenticated">{isAuthenticated.toString()}</div>
            <div data-testid="currentUser">
              {currentUser ? JSON.stringify(currentUser) : 'null'}
            </div>
            <button data-testid="refreshUser" onClick={refreshUser}>
              Refresh
            </button>
            <button
              data-testid="setUserFromCallback"
              onClick={() =>
                setUserFromCallback({
                  display_name: 'OAuth User',
                  email: 'oauth@example.com',
                })
              }
            >
              Set OAuth User
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatComponent;
